import { createPortal } from "react-dom";
import { RemoveScroll } from "react-remove-scroll";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "../ui/Button";
import { Logo } from "../ui/Logo";
import { OtpCodeInput } from "../ui/OtpCodeInput";
import { useDesktopAuthGate } from "../../domains/auth/hooks/useDesktopAuthGate";
import { OTP_CODE_LENGTH } from "../../domains/auth/hooks/useEmailOtp";
import type { DesktopCodeRequest } from "../../domains/auth/stores/desktopCodeRequestStore";
import type { OtpFailureReason } from "../../tools/analytics/domains/onboarding.analytics";

/**
 * The code half of the desktop door (Figma "D7.inscription.default",
 * "D7.inscription.code-expire").
 *
 * The door (`/signup` or `/login`) takes the address, then shows the app the visitor is signing into
 * with this card over it: the app is meant to be recognisable through the
 * blur, not readable. It does not close (no close control, the backdrop does
 * not dismiss) because there is nothing to do behind it: every query is gated
 * on the Firebase session, so the shell is a shell. The way out is "Use
 * another email", back to the door.
 *
 * Mounted by `AppRoutes` while `useDesktopAuthGateRequest` holds a request.
 */
export function DesktopAuthGateModal({
  request,
}: {
  request: DesktopCodeRequest;
}): React.JSX.Element {
  const { t } = useTranslation();
  const gate = useDesktopAuthGate(request);
  const isComplete = gate.code.length === OTP_CODE_LENGTH;

  return createPortal(
    <RemoveScroll enabled>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("Enter your code")}
        className="fixed inset-0 z-1000 flex items-center justify-center bg-ds-black/42 p-6 backdrop-blur-sm"
      >
        <div className="flex w-full max-w-lg flex-col gap-7 rounded-2xl bg-ds-white p-11 shadow-card">
          <Logo variant="no-tagline" tone="color" className="h-13 self-start" />

          <div className="flex flex-col gap-3">
            <h1 className="m-0 font-dm-sans text-heading-display font-semibold text-ds-black">
              {t("Enter your code")}
            </h1>
            <p className="m-0 font-dm-sans text-ui-body text-ds-slate-500">
              <Trans
                i18nKey="We sent a 6-digit code to <bold>{{email}}</bold><br />No password needed."
                t={t}
                values={{ email: request.email }}
                components={{
                  bold: <span className="font-bold" />,
                  br: <br />,
                }}
              />
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <OtpCodeInput
              value={gate.code}
              length={OTP_CODE_LENGTH}
              onChange={gate.setCode}
              onComplete={gate.onCodeComplete}
              hasError={gate.error !== null}
              // Mounted only while a code is awaited, so it is always on stage.
              autoFocus
              disabled={gate.isBusy}
              variant="desktop"
            />
            <div className="flex flex-col items-start gap-2 pt-2 font-dm-sans text-ui-note font-medium">
              {gate.error === null ? (
                // "Use another email" too, although the design draws it on a
                // refused code only: a mistyped address never receives a code,
                // so it never reaches that state, and the modal has no other
                // way out.
                <div className="flex gap-4 text-ds-mint-700">
                  <button
                    type="button"
                    className="border-none bg-transparent p-0 underline disabled:text-ds-slate-500 disabled:no-underline"
                    disabled={gate.cooldownRemaining > 0 || gate.isBusy}
                    onClick={gate.onResend}
                  >
                    {gate.cooldownRemaining > 0
                      ? t("Resend the code ({{seconds}}s)", {
                          seconds: gate.cooldownRemaining,
                        })
                      : t("Resend the code")}
                  </button>
                  <button
                    type="button"
                    className="border-none bg-transparent p-0 underline"
                    onClick={gate.onChangeEmail}
                  >
                    {t("Use another email")}
                  </button>
                </div>
              ) : (
                <>
                  <span className="text-ds-strawberry-700">
                    {failureMessage(t, gate.error)}
                  </span>
                  <div className="flex gap-4 text-ds-mint-800">
                    <button
                      type="button"
                      className="border-none bg-transparent p-0 underline disabled:text-ds-slate-500 disabled:no-underline"
                      disabled={gate.cooldownRemaining > 0 || gate.isBusy}
                      onClick={gate.onResend}
                    >
                      {gate.cooldownRemaining > 0
                        ? t("Send a new code ({{seconds}}s)", {
                            seconds: gate.cooldownRemaining,
                          })
                        : t("Send a new code")}
                    </button>
                    <button
                      type="button"
                      className="border-none bg-transparent p-0 underline"
                      onClick={gate.onChangeEmail}
                    >
                      {t("Use another email")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* A full code verifies on its own (`onComplete`). The button is
              there once there is something to submit, and stays, disabled, on
              a refused code: the way on is a new code, not this one again. */}
          {isComplete && (
            <Button
              type="button"
              variant="primary"
              size="md"
              className="w-full text-ui-body"
              disabled={gate.error !== null || gate.isBusy}
              onClick={() => {
                gate.onCodeComplete(gate.code);
              }}
            >
              {t("Continue")}
            </Button>
          )}
        </div>
      </div>
    </RemoveScroll>,
    document.body
  );
}

function failureMessage(
  t: (key: string) => string,
  reason: OtpFailureReason
): string {
  switch (reason) {
    case "wrong_code":
      return t("This code is not right.");
    case "expired":
      return t("This code has expired.");
    case "too_many_attempts":
      return t("Too many attempts.");
    case "rate_limited":
      return t("Too many requests. Wait a moment.");
    default:
      throw new Error(reason satisfies never);
  }
}
